import * as PIXI from "pixi.js";

import { EventSystem } from "@src/core/events/EventSystem";
import { IView } from "@src/core/game/components/IView";
import {
  Spine,
  TrackEntry,
  Animation,
  AnimationStateListener,
} from "@pixi/spine-pixi";
import { gameEngine } from "@src/game/GameEngine";
import { constants } from "@src/game/Constants";

export class SymbolPool {
  private pool: Symbol[] = [];

  constructor(initialPoolSize: number = 20) {
    for (let i = 0; i < initialPoolSize; i++) {
      this.pool.push(new Symbol("1"));
    }
  }

  public acquire(): Symbol {
    if (this.pool.length > 0) {
      const symbol = this.pool.pop();
      if (symbol) {
        symbol.view.alpha = 1;
        symbol.view.position.set(0, 0);
        symbol.view.scale.set(1);
        symbol.view.visible = true;
      }
      return symbol!;
    } else {
      return new Symbol("1");
    }
  }

  public release(symbol: Symbol) {
    symbol.view.removeFromParent();
    this.pool.push(symbol);
  }
}

export class Symbol implements IView {
  private _view: PIXI.Container = new PIXI.Container();
  public get view(): PIXI.Container {
    return this._view;
  }

  private _type!: string;
  public get type(): string {
    return this._type;
  }

  private _symbolSkeleton: Spine;

  private _multiplier!: string;
  public get multiplier(): string {
    return this._multiplier;
  }

  private _coinMultiplierText: PIXI.Text;
  private _stateListener!: AnimationStateListener;

  // public originalPosition!: PIXI.Point;
  // public originalParent!: PIXI.Container;

  public static readonly WIDTH: number = 130 as const;
  public static readonly HEIGHT: number = 135 as const;

  private static _pool: SymbolPool;
  public static get pool(): SymbolPool {
    if (!Symbol._pool) {
      throw new Error(
        "Symbol pool is not initialized. Call Symbol.createPool() first.",
      );
    }
    return Symbol._pool;
  }

  public static createPool(initialPoolSize: number = 20) {
    Symbol._pool = new SymbolPool(initialPoolSize);
  }

  public constructor(type: string) {
    // this._initializeComponents();
    // this._parentComponents();

    // this._setupEventListeners();

    this._symbolSkeleton = Spine.from({
      skeleton: "h1AtlasJson",
      atlas: "h1Atlas",
    });

    this.view.addChild(this._symbolSkeleton);

    this._coinMultiplierText = new PIXI.Text({
      text: "",
      style: {
        fontSize: 35,
        fontFamily: "gameFont",
        fill: 0xffbb1e,
        fontWeight: "bold",
        stroke: {
          color: 0x4d3300,
          width: 8,
          join: "round",
        },
      },
    });
    this._coinMultiplierText.anchor.set(0.5);
    this._coinMultiplierText.zIndex = 12;
    // this._multiplierText.x = 5;

    this.view.addChild(this._coinMultiplierText);
    this.setType(type.toString());
  }

  // private _initializeComponents(): void {}
  // private _parentComponents(): void {}
  // private _setupEventListeners(): void {
  //   EventSystem.addListener(
  //     constants.GameEvents.SYMBOL_REEL_STOPPED,
  //     this._onReelStopped.bind(this),
  //   );
  // }

  public setType(type: string): void {
    if (this._symbolSkeleton && this._stateListener) {
      this._symbolSkeleton.state.removeListener(this._stateListener);
    }

    this.view.removeChild(this._symbolSkeleton);
    this._symbolSkeleton.destroy();

    this._type = type;

    let symbolName: string = "";

    this._view.zIndex = constants.Layers.SYMBOL;
    this._coinMultiplierText.visible = false;

    switch (this._type) {
      case "1":
        symbolName = "wild";
        break;
      case "2":
        symbolName = "h4";
        break;
      case "3":
        symbolName = "h3";
        break;
      case "4":
        symbolName = "h2";
        break;
      case "5":
        symbolName = "h1";
        break;
      case "6":
        symbolName = "l4";
        break;
      case "7":
        symbolName = "l3";
        break;
      case "8":
        symbolName = "l2";
        break;
      case "9":
        symbolName = "l1";
        break;
      case "10":
        symbolName = "scatter";
        this.view.zIndex = 10;
        break;
      case "11":
      case "12":
      case "13":
        symbolName = "bag";
        break;
      default:
        this._type = "100";
        symbolName = "coin";
        this.view.zIndex = 11;
        break;
    }

    // Handle special naming
    if (symbolName[0] === "l") {
      this._symbolSkeleton = Spine.from({
        skeleton: "lowsAtlasJson",
        atlas: "lowsAtlas",
      });
      this._symbolSkeleton.skeleton.setSkinByName(symbolName);
    } else {
      this._symbolSkeleton = Spine.from({
        skeleton: `${symbolName}AtlasJson`,
        atlas: `${symbolName}Atlas`,
      });
    }

    this._symbolSkeleton.skeleton.setSlotsToSetupPose();
    this._symbolSkeleton.scale.set(0.12);
    this._symbolSkeleton.y = 0;
    this._symbolSkeleton.angle = 0;

    // wild or scatter skin setting
    if (this.type === "1" || this.type === "10") {
      // this.symbol.scale.set(0.36);
      this._symbolSkeleton.skeleton.setSkinByName("default");
    }
    if (this.type === "11") {
      this._symbolSkeleton.skeleton.setSkinByName("s1");
    } else if (this.type === "12") {
      this._symbolSkeleton.skeleton.setSkinByName("s2");
    } else if (this.type === "13") {
      this._symbolSkeleton.skeleton.setSkinByName("s3");
    }

    this.view.addChild(this._symbolSkeleton);
    this.idle();
    this._setupSkeletonEventListeners();
  }

  public getCoinMultiplierText(): string {
    return this._coinMultiplierText.text;
  }

  public setCoinMultiplierText(multiplier: string): void {
    if (!this.type.startsWith("100") || multiplier === "") {
      return;
    }

    const formattedMultiplier: string = gameEngine.formatter.format(
      Number.parseInt(multiplier),
    );

    this._coinMultiplierText.text = formattedMultiplier;
    this._coinMultiplierText.style.fontSize = 35;
    this._coinMultiplierText.visible = true;

    if (multiplier.length <= 2) {
      this._coinMultiplierText.style.fontSize = 31;
      return;
    }
    if (multiplier.length <= 3) {
      this._coinMultiplierText.style.fontSize = 28;
      return;
    }
    this._multiplier = multiplier;

    // if (multiplier === 1) {
    //   this._symbolSkeleton.skeleton.setSkinByName(`w`);
    // } else {
    //   this._symbolSkeleton.skeleton.setSkinByName(`x${multiplier}`);
    // }

    // this._symbolSkeleton.skeleton.setSlotsToSetupPose();
    // this.symbol.state.setAnimation(0, 'm_idle_on', true);
    // this.idle();
  }

  public playWildExpansionAnimation(): void {
    if (this.type !== "1") {
      return;
    }
    this._symbolSkeleton.state.setAnimation(0, "expand", false);
  }

  public playWinAnimation(): void {
    this._symbolSkeleton.state.setAnimation(0, "match", false);
    this._symbolSkeleton.state.addAnimation(0, "idle", true);

    this._view.tint = 0xffffff;

    //   EventSystem.invoke(
    //     constants.GameEvents.SYMBOL_WIN_ANIMATION_STARTED,
    //     this._view,
    //   );
  }

  public playLandAnimation(): void {
    this._symbolSkeleton.state.setAnimation(0, "land", false);
    this._symbolSkeleton.state.addAnimation(0, "idle", true);
  }

  public idle(): void {
    let animationName = "idle";
    // Get the animation duration (in frames or time)
    const animation =
      this._symbolSkeleton.skeleton.data.findAnimation(animationName);
    if (animation) {
      const duration = animation.duration;

      // Randomly pick a time between 0 and the animation duration
      const randomTime = Math.random() * duration;

      // Set the animation to play from the random time
      this._symbolSkeleton.state.setAnimation(
        0,
        animationName,
        true,
      ).timeScale = 0.5; // loop is set to true

      if (this.type === "10") {
        animationName = "land";

        this._symbolSkeleton.state.setAnimation(
          0,
          animationName,
          false,
        ).timeScale = 0;
      }

      this._symbolSkeleton.state.update(randomTime);
    }
  }

  private _setupSkeletonEventListeners(): void {
    this._stateListener = {
      complete: () => {
        const currentTrack: TrackEntry | null =
          this._symbolSkeleton.state.getCurrent(0);

        if (currentTrack) {
          const animation: Animation | null = currentTrack.animation;

          if (animation && animation.name.includes("match")) {
            if (this._type === "10") {
              EventSystem.invoke(
                constants.GameEvents.SCATTER_WIN_ANIMATION_ENDED,
                this._view,
              );
            } else {
              EventSystem.invoke(
                constants.GameEvents.SYMBOL_WIN_ANIMATION_ENDED,
                this._view,
              );
            }
          }
          if (animation && animation.name.includes("land")) {
            requestAnimationFrame(() => {
              const worldPos: PIXI.Point = this._view.toGlobal(
                new PIXI.Point(0, 0),
              );
              EventSystem.invoke(
                constants.GameEvents.SYMBOL_LANDED,
                this._type,
                worldPos.x,
                worldPos.y,
              );
            });
          }
        }
      },
    };

    this._symbolSkeleton.state.addListener(this._stateListener);
  }
}
